import { TouchableOpacity } from 'react-native';
import { Link, usePathname } from 'expo-router';
import { Image } from 'expo-image';
import { cssInterop } from 'nativewind';
import { useTheme } from '~/components/providers/ThemeProvider';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  MessageMultiple01Icon,
  Notification02Icon,
} from '@hugeicons-pro/core-solid-rounded';
import { UserProfileMenu } from '@/components/layout/UserProfileMenu';
import { ImagePattern } from '../ui/ImagePattern';
import { HStack } from '../gluestack/hstack';
import { VStack } from '../gluestack/vstack';
import { useSignalRUpdates } from '../providers/SignalRProvider';
import { Box } from '../gluestack/box';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { triggerPageRefresh, clearStoredScrollPosition } from '~/hooks/useInfiniteListUtils';

import { CommunitySelectorHeader } from '~/components/layout/CommunitySelectorHeader';
import { StagingApiBadge } from '@/components/ui/StagingApiBadge';

cssInterop(Image, { className: 'style' });

export function LargeScreenHeader() {
  const { getThemeColor, currentPattern } = useTheme();
  const { messageCount, notificationCount } = useSignalRUpdates();
  const pathname = usePathname();
  const secondaryColor = getThemeColor('secondary', '500');
  const insets = useSafeAreaInsets();
  const top = insets.top;
  const headerBaseHeight = 64; // matches previous h-16
  const headerHeight = headerBaseHeight + top;

  const handleNavPress = (pageKey: string) => {
    const isActive = pathname.startsWith(pageKey);

    clearStoredScrollPosition(pageKey);

    if (isActive) {
      triggerPageRefresh(pageKey);
    }
  };

  return (
    <HStack
      className="items-center justify-between bg-secondary-500 drop-shadow-lg z-10"
      style={{ height: headerHeight }}
    >
      <VStack className="absolute inset-0 overflow-hidden">
        <ImagePattern
          backgroundColor={secondaryColor}
          scale={currentPattern.scale}
          source={currentPattern.source}
        />
      </VStack>

      <HStack className="absolute inset-0 items-center justify-between pl-4 pr-4" style={{ top }}>
        <HStack className="items-center" space="sm">
          <CommunitySelectorHeader />
          <StagingApiBadge />
        </HStack>

        <HStack space="md" className="items-center justify-end">
          <Link href="/messages/inbox" asChild>
            <TouchableOpacity activeOpacity={0.7} onPress={() => handleNavPress('/messages/inbox')}>
              <HStack
                space="sm"
                className="items-center rounded-full bg-secondary-600 p-2 dark:bg-secondary-300"
              >
                {messageCount > 0 && (
                  <Text className="ml-2 text-xs font-bold uppercase text-white">
                    {messageCount}
                  </Text>
                )}
                <Box>
                  <HugeIconsWrapper icon={MessageMultiple01Icon} size={24} color="white" />
                  {messageCount > 0 && (
                    <Box className="absolute -right-0.5 -top-0.5 size-3 rounded-full bg-error-500" />
                  )}
                </Box>
              </HStack>
            </TouchableOpacity>
          </Link>

          <Link href="/notifications" asChild>
            <TouchableOpacity activeOpacity={0.7} onPress={() => handleNavPress('/notifications')}>
              <HStack
                space="sm"
                className="items-center rounded-full bg-secondary-600 p-2 dark:bg-secondary-300"
              >
                {notificationCount > 0 && (
                  <Text className="ml-2 text-xs font-bold uppercase text-white">
                    {notificationCount}
                  </Text>
                )}
                <Box>
                  <HugeIconsWrapper icon={Notification02Icon} size={24} color="white" />
                  {notificationCount > 0 && (
                    <Box className="absolute -right-0.5 -top-0.5 size-3 rounded-full bg-error-500" />
                  )}
                </Box>
              </HStack>
            </TouchableOpacity>
          </Link>

          <UserProfileMenu />
        </HStack>
      </HStack>
    </HStack>
  );
}
